<template>
  <PageError v-if="error" :error="error" :errorCode="errorCode" />

  <div v-else class="p-6 dark:text-white">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Packet Captures ({{ probeRows.length }} probe{{ probeRows.length !== 1 ? 's' : '' }})
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div class="mb-4 text-base flex space-x-3 justify-between">
        <button
          @click="$router.push({ name: 'AddCapture' })"
          class="bg-blue-700 text-white px-6 py-2 rounded-md hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
        >
          Add Capture Config
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Packet Captures..."
      @select="handleOrgSelect"
    />

    <div class="mb-6">
      <SearchBar
        v-model="searchQuery"
        placeholderText="Search by probe or capture name, interface, prefix..."
      />
    </div>

    <div v-if="probeRows.length === 0" class="text-center text-gray-500 py-12">
      No capture configurations yet. Create one with <b>Add Capture Config</b>.
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="row in probeRows"
        :key="row.probe_id"
        class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
      >
        <!-- Probe header row (color tokens mirror ProbeDetails tab buttons) -->
        <button
          type="button"
          @click="toggleProbe(row.probe_id)"
          :class="[
            'w-full flex items-center justify-between px-6 py-4 focus:outline-none transition-colors duration-150 ease-in-out border-l-4',
            expanded[row.probe_id]
              ? 'text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-700 border-blue-500'
              : 'text-gray-700 dark:text-gray-200 border-transparent hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700/60'
          ]"
        >
          <div class="flex items-center space-x-3">
            <svg
              class="w-5 h-5 transition-transform"
              :class="{ 'rotate-90': expanded[row.probe_id] }"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
            <span class="font-semibold text-lg text-left">{{ row.probe_name }}</span>
          </div>
          <div class="flex items-center space-x-4 text-sm">
            <span class="text-gray-600 dark:text-gray-300">
              {{ row.captures.length }} capture{{ row.captures.length !== 1 ? 's' : '' }}
            </span>
            <span
              class="px-2 py-1 text-xs rounded-full"
              :class="[
                row.enabled_count > 0
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-200 text-gray-700'
              ]"
            >
              {{ row.enabled_count }} enabled
            </span>
            <span
              v-if="row.pending_count > 0"
              class="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800"
            >
              {{ row.pending_count }} pending
            </span>

            <!-- Services chip -->
            <span
              v-if="telemetry[row.probe_id]"
              class="px-2 py-1 text-xs rounded-full"
              :class="servicesChipClass(telemetry[row.probe_id])"
            >
              {{ servicesChipLabel(telemetry[row.probe_id]) }}
            </span>
            <span v-else-if="telemetryLoading[row.probe_id]" class="text-xs text-gray-400">
              checking...
            </span>
            <span v-else-if="telemetryError[row.probe_id]" class="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800" :title="telemetryError[row.probe_id]">
              unreachable
            </span>

            <!-- Disk summary: limit (ring buffer reservation) vs actual usage -->
            <span
              v-if="telemetry[row.probe_id]"
              class="px-3 py-1 text-xs rounded-md bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 leading-tight text-left"
              :title="diskChipTitle(telemetry[row.probe_id])"
            >
              <div>
                <strong>Ring buffer limit:</strong>
                {{ formatBytes(telemetry[row.probe_id].budget.reservation_bytes) }}
              </div>
              <div class="pl-3">
                Pcap files currently on disk:
                {{ formatBytes(telemetry[row.probe_id].disk.captures_used_bytes) }}
                ({{ pctOfLimit(telemetry[row.probe_id]) }}% of limit)
              </div>
              <div>
                <strong>Probe disk overall:</strong>
                {{ formatBytes(telemetry[row.probe_id].disk.used_bytes) }}
                of {{ formatBytes(telemetry[row.probe_id].disk.total_bytes) }} used
                ({{ diskUsedPct(telemetry[row.probe_id]) }}%)
              </div>
            </span>

            <!-- Freshness -->
            <span
              v-if="telemetry[row.probe_id]"
              class="text-xs text-gray-400 dark:text-gray-500"
              :title="'Fetched at ' + telemetry[row.probe_id].fetched_at"
            >
              {{ formatRelativeTime(telemetry[row.probe_id].fetched_at) }}
            </span>
          </div>
        </button>

        <!-- Expanded captures section -->
        <div
          v-if="expanded[row.probe_id]"
          class="border-t border-gray-200 dark:border-gray-700"
        >
          <!-- Probe telemetry panel (live) -->
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md">
            <div v-if="telemetryLoading[row.probe_id] && !telemetry[row.probe_id]" class="text-sm text-gray-500 flex items-center space-x-2">
              <svg class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z" />
              </svg>
              <span>Fetching live state from probe...</span>
            </div>

            <div v-else-if="telemetryError[row.probe_id]" class="text-sm text-red-600">
              {{ telemetryError[row.probe_id] }}
            </div>

            <div v-else-if="telemetry[row.probe_id]">
              <div class="flex items-start justify-between mb-3">
                <div>
                  <div class="text-sm font-medium">Disk usage</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-1 space-y-0.5">
                    <div>
                      <strong>Ring buffer limit:</strong>
                      {{ formatBytes(telemetry[row.probe_id].budget.reservation_bytes) }}
                      <span
                        class="text-gray-400 cursor-help"
                        title="Maximum disk this probe will ever consume for captures, based on the sum of filesize * file_count across all enabled captures with size rotation."
                      >(?)</span>
                    </div>
                    <div class="pl-3">
                      Pcap files currently on disk:
                      {{ formatBytes(telemetry[row.probe_id].disk.captures_used_bytes) }}
                      ({{ pctOfLimit(telemetry[row.probe_id]) }}% of limit)
                    </div>
                    <div>
                      <strong>Probe disk overall:</strong>
                      {{ formatBytes(telemetry[row.probe_id].disk.used_bytes) }}
                      of {{ formatBytes(telemetry[row.probe_id].disk.total_bytes) }} used
                      ({{ diskUsedPct(telemetry[row.probe_id]) }}%)
                    </div>
                    <div class="text-[10px] text-gray-400 pt-1">
                      Headroom available for new captures:
                      {{ formatBytes(telemetry[row.probe_id].budget.budget_bytes) }}
                      (90% of free space)
                    </div>
                  </div>
                </div>
                <button
                  class="text-xs text-blue-600 hover:text-blue-800"
                  @click="refreshTelemetry(row.probe_id)"
                  :disabled="telemetryLoading[row.probe_id]"
                >
                  {{ telemetryLoading[row.probe_id] ? 'refreshing...' : 'refresh' }}
                </button>
              </div>

              <!-- Disk bar -->
              <div class="w-full bg-gray-200 dark:bg-gray-700 rounded h-3 overflow-hidden mb-1">
                <div
                  class="h-3 transition-all"
                  :class="[
                    telemetry[row.probe_id].budget.over_budget
                      ? 'bg-red-600'
                      : diskPctNum(telemetry[row.probe_id]) > 75
                        ? 'bg-yellow-500'
                        : 'bg-blue-500',
                  ]"
                  :style="{ width: Math.min(diskPctNum(telemetry[row.probe_id]), 100) + '%' }"
                ></div>
              </div>
              <div class="text-xs text-gray-500">
                {{ diskPct(telemetry[row.probe_id]) }}% of capture budget used
                <span v-if="telemetry[row.probe_id].budget.over_budget" class="text-red-600 font-medium">
                  (over budget; reduce filesize or file count on at least one capture)
                </span>
              </div>

              <!-- Per-service list -->
              <div class="mt-4">
                <div class="text-sm font-medium mb-2">Services on probe</div>
                <div v-if="telemetry[row.probe_id].services.details.length === 0" class="text-xs text-gray-500">
                  No capture services expected on this probe yet.
                </div>
                <ul v-else class="space-y-1">
                  <li
                    v-for="svc in telemetry[row.probe_id].services.details"
                    :key="svc.unit"
                    class="flex items-center space-x-3 text-xs"
                  >
                    <span
                      class="inline-block w-2 h-2 rounded-full flex-shrink-0"
                      :class="serviceDotClass(svc.status)"
                    ></span>
                    <code class="font-mono">{{ svc.unit }}</code>
                    <span class="text-gray-500">{{ svc.reason }}</span>
                    <span class="ml-auto" :class="serviceTextClass(svc.status)">
                      {{ serviceLabel(svc) }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <table class="min-w-full text-sm">
            <thead>
              <tr class="text-left border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400">
                <th class="px-6 py-2">Name</th>
                <th class="px-3 py-2">Interface</th>
                <th class="px-3 py-2">BPF</th>
                <th class="px-3 py-2">Prefix</th>
                <th class="px-3 py-2">State</th>
                <th class="px-3 py-2">Approval</th>
                <th class="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="cap in row.captures"
                :key="cap.id"
                class="border-b border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 cursor-pointer"
                @click="$router.push({ name: 'CaptureDetails', params: { id: cap.id }, query: tenant.addSchemaParam() })"
              >
                <td class="px-6 py-2 font-medium">{{ cap.name }}</td>
                <td class="px-3 py-2 font-mono text-xs">{{ cap.interface }}</td>
                <td class="px-3 py-2 font-mono text-xs truncate max-w-xs">{{ cap.bpf_filter || '(none)' }}</td>
                <td class="px-3 py-2 font-mono text-xs">{{ cap.file_prefix }}</td>
                <td class="px-3 py-2">
                  <span
                    class="px-2 py-0.5 text-xs rounded-full"
                    :class="[
                      cap.enabled
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-200 text-gray-700'
                    ]"
                  >
                    {{ cap.enabled ? 'Enabled' : 'Disabled' }}
                  </span>
                </td>
                <td class="px-3 py-2">
                  <span
                    class="px-2 py-0.5 text-xs rounded-full"
                    :class="[
                      cap.pending
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    ]"
                  >
                    {{ cap.pending ? 'Pending' : 'Approved' }}
                  </span>
                </td>
                <td class="px-3 py-2 text-right space-x-2" @click.stop>
                  <button
                    class="text-blue-600 hover:text-blue-800 text-xs"
                    @click="$router.push({ name: 'CaptureDetails', params: { id: cap.id }, query: tenant.addSchemaParam() })"
                  >
                    Edit
                  </button>
                  <button
                    class="text-yellow-600 hover:text-yellow-800 text-xs"
                    @click="toggleEnabled(cap)"
                  >
                    {{ cap.enabled ? 'Disable' : 'Enable' }}
                  </button>
                  <button
                    class="text-red-600 hover:text-red-800 text-xs"
                    @click="confirmDelete(cap)"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="px-6 py-3">
            <button
              class="text-blue-600 hover:text-blue-800 text-sm font-medium"
              @click="$router.push({ name: 'AddCapture', query: { ...tenant.addSchemaParam(), probe: row.probe_id } })"
            >
              + Add capture to this probe
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="deletingItem">
      <div
        @click.self="deletingItem = null"
        class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 w-[500px] max-w-[90%]">
          <h3 class="text-lg font-semibold mb-3">Delete capture config?</h3>
          <p class="mb-4 text-sm text-gray-600 dark:text-gray-300">
            <b>{{ deletingItem.name }}</b> on <b>{{ deletingItem.probe_name }}</b> will be marked for deletion and removed on the next commit.
          </p>
          <div class="flex justify-end space-x-2">
            <button
              class="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-100"
              @click="deletingItem = null"
            >Cancel</button>
            <button
              class="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
              @click="doDelete"
            >Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAllCaptureConfigs,
  deleteCaptureConfig,
  updateCaptureConfig,
  getProbeTelemetry,
  getProbeTelemetryBulk,
} from "@/services/captureService";
import SearchBar from "@/components/SearchBar.vue";
import PageError from "@/components/PageError.vue";
import { formatError } from "@/utils/errorMessages";
import { formatRelativeTime } from "@/utils/relativeTime";
import { formatBytes } from "@/utils/formatBytes";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";
import OrganizationTabs from "@/components/OrganizationTabs.vue";

export default {
  name: "CaptureView",
  components: { SearchBar, PageError, OrganizationTabs },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      configs: [],
      searchQuery: "",
      expanded: {},
      deletingItem: null,
      error: "",
      errorCode: "",
      // Telemetry state per probe_id. Three parallel maps so the template can
      // distinguish: "never fetched" vs "in-flight" vs "have data" vs "errored".
      telemetry: {},
      telemetryLoading: {},
      telemetryError: {},
    };
  },
  computed: {
    user() { return useStore.getUser(); },
    isServiceProviderUser() { return useStore.isServiceProviderUser(); },
    isFiltered() { return !!this.searchQuery; },
    filteredConfigs() {
      const q = (this.searchQuery || "").toLowerCase();
      if (!q) return this.configs;
      return (this.configs || []).filter((c) =>
        (c.name || "").toLowerCase().includes(q)
        || (c.probe_name || "").toLowerCase().includes(q)
        || (c.interface || "").toLowerCase().includes(q)
        || (c.file_prefix || "").toLowerCase().includes(q)
        || (c.bpf_filter || "").toLowerCase().includes(q)
      );
    },
    probeRows() {
      const groups = new Map();
      for (const c of this.filteredConfigs) {
        const key = c.probe;
        if (!groups.has(key)) {
          groups.set(key, {
            probe_id: c.probe,
            probe_name: c.probe_name,
            captures: [],
            enabled_count: 0,
            pending_count: 0,
          });
        }
        const g = groups.get(key);
        g.captures.push(c);
        if (c.enabled) g.enabled_count += 1;
        if (c.pending) g.pending_count += 1;
      }
      const rows = Array.from(groups.values());
      rows.sort((a, b) => (a.probe_name || "").localeCompare(b.probe_name || ""));
      for (const row of rows) {
        row.captures.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      }
      return rows;
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
      // Org already pinned (back-navigation or ?client= refresh):
      // loadInitialData() skips the fetch when the org is unchanged.
      await this.tenant.syncUrlParam();
      await this.fetchConfigs(this.tenant.currentSchemaName.value);
    } else {
      await this.tenant.loadInitialData(this.fetchConfigs);
    }
    this.fetchAllTelemetryCached();
  },
  methods: {
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchConfigs);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error('Organization select error:', error);
      }
    },
    async fetchConfigs(schemaName = null) {
      try {
        const configs = await getAllCaptureConfigs(schemaName);
        this.configs = [...configs];
      } catch (error) {
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    toggleProbe(probeId) {
      const wasExpanded = !!this.expanded[probeId];
      this.expanded = { ...this.expanded, [probeId]: !wasExpanded };
      // Expanding triggers a live refresh so the panel shows current state.
      if (!wasExpanded) {
        this.refreshTelemetry(probeId);
      }
    },
    // One-shot bulk fetch for every probe in the current list. Single POST
    // collapses N per-probe GETs into one salt list-target dispatch server
    // side, so page load at 100 probes is ~3-5 s instead of ~150 s on a cold
    // cache. Per-probe refresh (user expanding a row) still uses the single
    // endpoint via loadTelemetry().
    async fetchAllTelemetryCached() {
      const probeIds = this.probeRows.map(r => r.probe_id);
      if (probeIds.length === 0) return;
      for (const id of probeIds) {
        this.telemetryLoading = { ...this.telemetryLoading, [id]: true };
        this.telemetryError = { ...this.telemetryError, [id]: "" };
      }
      try {
        const { results, missing } = await getProbeTelemetryBulk(probeIds, {
          schemaName: this.tenant.currentSchemaName?.value,
        });
        const nextTelemetry = { ...this.telemetry };
        for (const [pid, payload] of Object.entries(results || {})) {
          nextTelemetry[Number(pid)] = payload;
        }
        this.telemetry = nextTelemetry;
        if (Array.isArray(missing)) {
          const nextError = { ...this.telemetryError };
          for (const pid of missing) {
            nextError[pid] = "probe unreachable";
          }
          this.telemetryError = nextError;
        }
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || "probe unreachable";
        const nextError = { ...this.telemetryError };
        for (const id of probeIds) {
          if (!this.telemetry[id]) {
            nextError[id] = msg;
          }
        }
        this.telemetryError = nextError;
      } finally {
        const nextLoading = { ...this.telemetryLoading };
        for (const id of probeIds) {
          nextLoading[id] = false;
        }
        this.telemetryLoading = nextLoading;
      }
    },
    refreshTelemetry(probeId) {
      this.loadTelemetry(probeId, { refresh: true });
    },
    async loadTelemetry(probeId, { refresh } = { refresh: false }) {
      this.telemetryLoading = { ...this.telemetryLoading, [probeId]: true };
      this.telemetryError = { ...this.telemetryError, [probeId]: "" };
      try {
        const data = await getProbeTelemetry(probeId, {
          refresh,
          schemaName: this.tenant.currentSchemaName?.value,
        });
        this.telemetry = { ...this.telemetry, [probeId]: data };
      } catch (e) {
        const msg = e?.response?.data?.error || e.message || "probe unreachable";
        this.telemetryError = { ...this.telemetryError, [probeId]: msg };
      } finally {
        this.telemetryLoading = { ...this.telemetryLoading, [probeId]: false };
      }
    },
    // ----- Services chip -----
    servicesChipLabel(t) {
      const s = t.services;
      if (s.failed > 0) return `${s.failed} failed`;
      if (s.not_yet_committed > 0) return `${s.not_yet_committed} pending commit`;
      if (s.stopped_by_user > 0 && s.running === 0) return `${s.stopped_by_user} stopped by user`;
      if (s.running > 0) return "services OK";
      return "no services";
    },
    servicesChipClass(t) {
      const s = t.services;
      if (s.failed > 0) return "bg-red-100 text-red-800";
      if (s.not_yet_committed > 0) return "bg-blue-100 text-blue-800";
      if (s.stopped_by_user > 0 && s.running === 0) return "bg-gray-200 text-gray-700";
      if (s.running > 0) return "bg-green-100 text-green-800";
      return "bg-gray-200 text-gray-700";
    },
    // ----- Per-service row -----
    serviceDotClass(status) {
      if (status === "running") return "bg-green-500";
      if (status === "stopped_by_user") return "bg-gray-400";
      if (status === "not_yet_committed") return "bg-blue-400";
      return "bg-red-500";
    },
    serviceTextClass(status) {
      if (status === "running") return "text-green-700 dark:text-green-400";
      if (status === "stopped_by_user") return "text-gray-500 dark:text-gray-400";
      if (status === "not_yet_committed") return "text-blue-700 dark:text-blue-400";
      return "text-red-700 dark:text-red-400";
    },
    serviceLabel(svc) {
      if (svc.status === "running") return "active";
      if (svc.status === "stopped_by_user") return "stopped by user";
      if (svc.status === "not_yet_committed") return "not yet committed";
      if (svc.active === "activating") return "restart looping";
      return `${svc.active} (expected active)`;
    },
    // ----- Disk chip -----
    diskPctNum(t) {
      // Bar fill tracks "pcap files on disk" as a fraction of the ring buffer
      // limit, matching the "(x% of limit)" copy right above it.
      const limit = t.budget.reservation_bytes || 0;
      const used = t.disk.captures_used_bytes || 0;
      if (!limit) return 0;
      return (used / limit) * 100;
    },
    diskPct(t) {
      const n = this.diskPctNum(t);
      if (n >= 10) return n.toFixed(0);
      return n.toFixed(1);
    },
    pctOfLimit(t) {
      const limit = t.budget.reservation_bytes || 0;
      const used = t.disk.captures_used_bytes || 0;
      if (!limit) return 0;
      const n = (used / limit) * 100;
      return n >= 10 ? n.toFixed(0) : n.toFixed(1);
    },
    diskUsedPct(t) {
      const total = t.disk.total_bytes || 0;
      const used = t.disk.used_bytes || 0;
      if (!total) return 0;
      return Math.round((used / total) * 100);
    },
    diskChipTitle(t) {
      return (
        `Ring buffer limit: ${formatBytes(t.budget.reservation_bytes)}\n` +
        `Pcap files on disk: ${formatBytes(t.disk.captures_used_bytes)}\n` +
        `Disk total: ${formatBytes(t.disk.total_bytes)}\n` +
        `Disk used: ${formatBytes(t.disk.used_bytes)}\n` +
        `Capture headroom (90% of free): ${formatBytes(t.budget.budget_bytes)}`
      );
    },
    formatBytes,
    formatRelativeTime,
    async toggleEnabled(cap) {
      try {
        await updateCaptureConfig(cap.id, { enabled: !cap.enabled }, this.tenant.currentSchemaName?.value);
        await this.fetchConfigs(this.tenant.currentSchemaName?.value);
      } catch (e) {
        alert("Failed to toggle capture state.");
      }
    },
    confirmDelete(cap) {
      this.deletingItem = cap;
    },
    async doDelete() {
      const cap = this.deletingItem;
      if (!cap) return;
      try {
        await deleteCaptureConfig(cap.id, this.tenant.currentSchemaName?.value);
        this.deletingItem = null;
        await this.fetchConfigs(this.tenant.currentSchemaName?.value);
      } catch (e) {
        alert("Delete failed.");
      }
    },
  },
};
</script>
